import { useEffect, useRef } from "react";

const clamp = (v) => Math.max(-1, Math.min(1, v));

// Parallax 3D de la carta del detalle: puntero → giroscopio → idle. Un solo rAF, sin setState por frame.
export const HitCardTilt = ({ children, className = "" }) => {
  const stage = useRef(null), card = useRef(null), holo = useRef(null), glare = useRef(null);
  useEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      card.current.style.transform = "rotateY(-10deg) rotateX(4deg)";
      return undefined;
    }
    const layers = [...el.querySelectorAll("[data-depth]")];
    let tx = 0, ty = 0, x = 0, y = 0, raf = 0, visible = true, lastPointer = 0, gyro = null, asked = false;
    const t0 = performance.now();
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      tx = clamp(((p.clientX - r.left) / r.width) * 2 - 1);
      ty = clamp(((p.clientY - r.top) / r.height) * 2 - 1);
      lastPointer = performance.now();
    };
    const onOrient = (e) => {
      if (e.gamma == null) return;
      gyro = { x: clamp(e.gamma / 25), y: clamp((e.beta - 45) / 25) };
    };
    const askGyro = () => {
      if (asked) return;
      asked = true;
      const D = window.DeviceOrientationEvent;
      if (D && typeof D.requestPermission === "function") {
        D.requestPermission().then((s) => { if (s === "granted") window.addEventListener("deviceorientation", onOrient); }).catch(() => {});
      }
    };
    const D = window.DeviceOrientationEvent;
    if (D && typeof D.requestPermission !== "function") window.addEventListener("deviceorientation", onOrient);
    const frame = (now) => {
      raf = 0;
      if (!visible || document.hidden) return;
      const t = (now - t0) / 1000;
      if (now - lastPointer > 1800) {
        if (gyro) { tx = gyro.x; ty = gyro.y; } else { tx = Math.sin(t * 0.7) * 0.55; ty = Math.cos(t * 0.9) * 0.35; }
      }
      x += (tx - x) * 0.08;
      y += (ty - y) * 0.08;
      card.current.style.transform = `translateY(${Math.sin(t * 1.2) * 5}px) rotateY(${x * 16}deg) rotateX(${-y * 12}deg)`;
      holo.current.style.backgroundPosition = `${50 + x * 45}% ${50 + y * 45}%`;
      glare.current.style.background = `radial-gradient(circle at ${50 + x * 50}% ${50 + y * 50}%, rgba(255,255,255,.45), transparent 55%)`;
      layers.forEach((l) => { const d = +l.dataset.depth; l.style.transform = `translate(${x * d}px, ${y * d}px)`; });
      raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!raf && visible && !document.hidden) raf = requestAnimationFrame(frame); };
    const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; start(); });
    io.observe(el);
    const onVis = () => start();
    el.addEventListener("pointermove", onMove);
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("pointerdown", askGyro);
    document.addEventListener("visibilitychange", onVis);
    start();
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("pointerdown", askGyro);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("deviceorientation", onOrient);
    };
  }, []);

  const corner = "absolute w-5 h-5 border-[#FF4438]";
  return (
    <div ref={stage} className={`relative grid place-items-center [perspective:900px] lg:[perspective:1100px] select-none ${className}`} data-testid="card-tilt-stage">
      <div data-depth="-22" aria-hidden className="absolute w-[300px] h-[300px] lg:w-[480px] lg:h-[480px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(255,68,56,.35), transparent)" }} />
      <span data-depth="14" aria-hidden className="absolute left-[12%] top-[18%] w-2 h-2 bg-[#FF4438] shadow-[0_0_12px_#FF4438] pointer-events-none" />
      <span data-depth="26" aria-hidden className="absolute right-[10%] top-[38%] w-1.5 h-1.5 bg-[#FF4438] shadow-[0_0_10px_#FF4438] pointer-events-none" />
      <span data-depth="34" aria-hidden className="absolute left-[20%] bottom-[16%] w-1.5 h-1.5 bg-white/70 pointer-events-none" />
      <div ref={card} className="relative w-[200px] h-[274px] lg:w-[340px] lg:h-[466px] [transform-style:preserve-3d] will-change-transform" style={{ transform: "rotateY(-10deg) rotateX(4deg)" }} data-testid="card-tilt">
        <div className="absolute inset-0 rounded-[12px] lg:rounded-[18px] overflow-hidden bg-[#0a0a0a] shadow-[0_30px_60px_-20px_rgba(255,68,56,.55)]">
          {children}
          <div ref={holo} aria-hidden className="absolute inset-0 pointer-events-none opacity-40"
            style={{ backgroundImage: "linear-gradient(115deg, transparent 20%, rgba(255,68,56,.55) 38%, rgba(255,255,255,.6) 50%, rgba(34,211,238,.55) 62%, transparent 80%)", backgroundSize: "260% 260%", mixBlendMode: "screen" }} />
          <div ref={glare} aria-hidden className="absolute inset-0 pointer-events-none" style={{ mixBlendMode: "overlay" }} />
        </div>
        <span aria-hidden className={`${corner} -left-3 -top-3 border-l-2 border-t-2`} style={{ transform: "translateZ(36px)" }} />
        <span aria-hidden className={`${corner} -right-3 -top-3 border-r-2 border-t-2`} style={{ transform: "translateZ(36px)" }} />
        <span aria-hidden className={`${corner} -left-3 -bottom-3 border-l-2 border-b-2`} style={{ transform: "translateZ(36px)" }} />
        <span aria-hidden className={`${corner} -right-3 -bottom-3 border-r-2 border-b-2`} style={{ transform: "translateZ(36px)" }} />
      </div>
    </div>
  );
};
